import { supabase } from '@/lib/supabase';
import type { OrgSubscription, SubscriptionStatus, SubscriptionTier } from '@/lib/types';

/**
 * Billing data layer (0074 + 0079).
 *
 * 0078 moved the catalog to a DB table; getCatalog() in src/lib/catalog.ts
 * is the source of truth for /pricing and /billing. The TIER_CATALOG
 * constant below mirrors today's seed values and is used as a fallback
 * if the DB read fails AND for the Stat-style helpers that don't fetch.
 */

export async function getMySubscription(): Promise<OrgSubscription | null> {
  const { data, error } = await supabase
    .from('org_subscriptions')
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as OrgSubscription | null;
}

// ----- Pricing fallback (matches /pricing seed; DB catalog overrides) -----

export const TIER_CATALOG: Record<
  SubscriptionTier,
  {
    name: string;
    monthlyCAD: number | null;
    annualCAD: number | null;
    blurb: string;
    features: string[];
    limits: string;
    cta: 'start_trial' | 'contact_sales';
  }
> = {
  standard: {
    name: 'Standard',
    monthlyCAD: 59,
    annualCAD: 49,
    blurb: 'Everything one independent pharmacy needs to run scheduling, time-off, and the audit trail.',
    features: [
      'Monthly schedule with drag-and-drop shifts',
      'Time-off requests with approval flow',
      'Print-ready single-page month view',
      'Append-only audit log',
      'Canadian hosting (ca-central-1)',
      'Up to 25 staff per location',
      'Email support, next business day',
    ],
    limits: 'Best for single-location pharmacies.',
    cta: 'start_trial',
  },
  pro: {
    name: 'Pro',
    monthlyCAD: 89,
    annualCAD: 79,
    blurb: 'For owners running multiple pharmacies who want a single dashboard and priority support.',
    features: [
      'Everything in Standard',
      'Multi-location consolidated view (coming soon)',
      'SMS shift reminders (coming soon)',
      'Calendar export — Google / Outlook (coming soon)',
      'Priority support, same business day',
    ],
    limits: 'Per-location pricing. Earn the upgrade as features ship.',
    cta: 'start_trial',
  },
  enterprise: {
    name: 'Enterprise',
    monthlyCAD: null,
    annualCAD: null,
    blurb: 'Banner chains and operators with three or more locations. Per-location rate, annual contract, dedicated onboarding.',
    features: [
      'Everything in Pro',
      'Custom onboarding and migration',
      'Dedicated account manager',
      'Service level agreement',
      'Volume pricing per location',
    ],
    limits: 'Talk to us. We tailor scope to the chain.',
    cta: 'contact_sales',
  },
};

export const TRIAL_DAYS = 30;
export const PILOT_DAYS = 90;
export const ANNUAL_SAVINGS_PERCENT = 17;
export const SALES_EMAIL = 'sales@pharma-sync.ca';

// ----- Display helpers ----------------------------------------------------

export function statusLabel(status: SubscriptionStatus): string {
  switch (status) {
    case 'trial': return 'Trial';
    case 'pilot': return 'Pilot';
    case 'active': return 'Active';
    case 'past_due': return 'Past due';
    case 'canceled': return 'Canceled';
  }
}

export function tierLabel(tier: SubscriptionTier | null): string {
  if (!tier) return '—';
  return tier === 'standard' ? 'Standard' : tier === 'pro' ? 'Pro' : 'Enterprise';
}

export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.ceil((then - now) / (1000 * 60 * 60 * 24));
}

export function formatCAD(amount: number): string {
  return `$${amount.toFixed(0)} CAD`;
}
